import { Alert } from '../../components/ui'
import type { Listing } from '../../api/objects'
import { t } from '../../i18n'
import { fullTime } from '../../format'
import { listingStatusLabel, verificationFactLabel, verificationMethodLabel } from '../../labels'

/**
 * How old the stored check is, and what that means for a client.
 *
 * ADR 0008: availability is whatever the stored check says. While the check is
 * good the assistant may answer from it; once it is not, the assistant does not
 * claim the flat is free and does not claim it is gone — it says the check is
 * being made. This block is that rule on screen, which is why an expired check
 * shouts and a fresh one does not.
 */

export type FreshnessState =
  /** Nobody has ever checked. */
  | 'never'
  /** The stored check is still good. */
  | 'valid'
  /** Still good, but not for long enough to lean on tomorrow. */
  | 'expiring'
  /** The check ran out. Not "gone": re-check before offering. */
  | 'expired'

/** A check inside this window is one somebody should renew today. */
const EXPIRING_WITHIN_MS = 24 * 60 * 60 * 1000

/**
 * Read the state off the two timestamps.
 *
 * The interval is half-open, like `domain/listing.ValidAt`: a check is good
 * until its expiry and not at it. Getting that boundary wrong is how a listing
 * stays offerable for longer than the lifetime somebody configured.
 */
export function freshnessState(listing: Listing, now: Date): FreshnessState {
  if (listing.verifiedAt === undefined || listing.verificationExpiresAt === undefined) return 'never'

  const expires = new Date(listing.verificationExpiresAt).getTime()
  if (Number.isNaN(expires)) return 'never'

  if (expires <= now.getTime()) return 'expired'
  return expires - now.getTime() <= EXPIRING_WITHIN_MS ? 'expiring' : 'valid'
}

export function FreshnessLine({
  listing,
  now = new Date(),
}: {
  readonly listing: Listing
  readonly now?: Date | undefined
}) {
  const state = freshnessState(listing, now)
  const verification = listing.latestVerification

  return (
    <div className="freshness">
      {state === 'valid' || state === 'expiring' ? (
        <p className={state === 'valid' ? 'freshness__line' : 'freshness__line freshness__line--warn'}>
          <span>{t('freshness.verifiedAt', { time: fullTime(listing.verifiedAt) })}</span>
          <span>{t('freshness.until', { time: fullTime(listing.verificationExpiresAt) })}</span>
          {verification === undefined ? null : (
            <span>{t('freshness.method', { method: verificationMethodLabel(verification.method) })}</span>
          )}
          {verification === undefined || verification.factsConfirmed.length === 0 ? null : (
            <span>
              {t('freshness.facts', {
                facts: verification.factsConfirmed.map(verificationFactLabel).join(', '),
              })}
            </span>
          )}
        </p>
      ) : null}

      {state === 'expiring' ? (
        <p className="freshness__hint">
          {t('freshness.expiringSoon', { time: fullTime(listing.verificationExpiresAt) })}
        </p>
      ) : null}

      {/*
        Loud on purpose, and in words rather than as a grey status. This is the
        difference between answering a client and telling them we are checking,
        and the manager reading this card is about to do one of the two.
      */}
      {state === 'expired' || state === 'never' ? (
        <Alert tone="warning">
          <div>
            <strong>
              {state === 'expired'
                ? t('freshness.expired', { time: fullTime(listing.verificationExpiresAt) })
                : t('freshness.never')}
            </strong>
            <div>{t('freshness.consequence')}</div>
          </div>
        </Alert>
      ) : null}

      {/*
        A check that is still good on a listing that has moved since. It does not
        expire the check by itself — that is why this is a note and not an alert —
        but a price that changed after the last call is the thing a manager must
        look at before quoting it.
      */}
      {listing.changedSinceCheck && (state === 'valid' || state === 'expiring') ? (
        <p className="freshness__hint">{t('freshness.changedSinceCheck')}</p>
      ) : null}

      {/*
        The check is good and the offer still cannot go out: reserved, on hold,
        already let. The server decides `offerable`; this only says which of the
        two reasons it was, so that "verified" and "may be offered" stop looking
        like the same word.
      */}
      {!listing.offerable && (state === 'valid' || state === 'expiring') ? (
        <p className="freshness__hint freshness__hint--warn">
          {t('freshness.notOfferableStatus', { status: listingStatusLabel(listing.status) })}
        </p>
      ) : null}
    </div>
  )
}
